/**
 * GpsQueueService — Offline SQLite queue for background GPS tracking points.
 *
 * Buffers location points captured by @capgo/background-geolocation into the
 * `gps_locations` table so a network outage never loses the technician's trail.
 * GpsTrackingService drains this queue in batches of up to 50 to
 * POST /v1/gps/locations when connectivity is available.
 *
 * Caps the queue at MAX_QUEUE points, discarding the oldest first (Req 2.4 /
 * 15.9 of the gps-route-optimization spec; fsm-mobile-platform task 25.4).
 * Mirrors the InventoryLocalService pattern (dedicated service over
 * DatabaseService, parameterized SQL, `synced` flag).
 *
 * @see spec gps-route-optimization Req 2.3, 2.4, 15.6, 15.9
 */
import { Injectable, inject } from "@angular/core";
import { DatabaseService } from "../../offline/database.service";

/** A single buffered GPS point, as stored in the `gps_locations` table. */
export interface QueuedGpsPoint {
  localId: string;
  lat: number;
  lng: number;
  /** ISO 8601 timestamp (what the backend expects as the point `ts`). */
  ts: string;
  speed: number | null;
  heading: number | null;
  accuracy: number;
  /** Battery level 0–100. */
  battery: number;
  /**
   * OT que el técnico ejecutaba al capturar el punto, para atribuir el
   * recorrido a la orden (spec gps-route-per-order Req 1). Opcional.
   */
  workOrderId?: string;
}

/** Maximum points retained in the offline queue (Req 15.9). */
export const MAX_QUEUE = 1000;

/** Maximum points sent per POST /v1/gps/locations batch (Req 2.3 / 15.6). */
export const MAX_BATCH = 50;

@Injectable({ providedIn: "root" })
export class GpsQueueService {
  private readonly db = inject(DatabaseService);

  /**
   * Persist one captured point into the offline queue, then enforce the
   * MAX_QUEUE cap by deleting the oldest points first (by `ts`). The cap counts
   * only unsynced rows; synced rows are removed by markSynced().
   */
  async enqueue(point: QueuedGpsPoint): Promise<void> {
    await this.db.run(
      `INSERT INTO gps_locations
         (localId, lat, lng, ts, speed, heading, accuracy, battery, workOrderId, synced, createdAt)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 0, ?)`,
      [
        point.localId,
        point.lat,
        point.lng,
        point.ts,
        point.speed,
        point.heading,
        point.accuracy,
        point.battery,
        point.workOrderId ?? null,
        new Date().toISOString(),
      ],
    );
    await this.enforceCap();
  }

  /**
   * Delete the oldest unsynced points when the queue exceeds MAX_QUEUE.
   * Oldest-first by `ts` (Req 15.9: "discarding oldest points first").
   */
  private async enforceCap(): Promise<void> {
    const rows = await this.db.query<{ cnt: number }>(
      "SELECT COUNT(*) as cnt FROM gps_locations WHERE synced = 0",
    );
    const count = rows[0]?.cnt ?? 0;
    if (count <= MAX_QUEUE) return;

    const overflow = count - MAX_QUEUE;
    // Select the N oldest unsynced points and delete them by id. A subquery in
    // DELETE is avoided so the IndexedDB web fallback (which parses simple SQL)
    // handles it too.
    const oldest = await this.db.query<{ localId: string }>(
      "SELECT localId FROM gps_locations WHERE synced = 0 ORDER BY ts ASC LIMIT ?",
      [overflow],
    );
    for (const row of oldest) {
      await this.db.run("DELETE FROM gps_locations WHERE localId = ?", [
        row.localId,
      ]);
    }
  }

  /**
   * Fetch up to MAX_BATCH unsynced points, oldest first (FIFO flush order).
   */
  async getUnsyncedBatch(limit: number = MAX_BATCH): Promise<QueuedGpsPoint[]> {
    const rows = await this.db.query<{
      localId: string;
      lat: number;
      lng: number;
      ts: string;
      speed: number | null;
      heading: number | null;
      accuracy: number;
      battery: number;
      workOrderId: string | null;
    }>(
      "SELECT localId, lat, lng, ts, speed, heading, accuracy, battery, workOrderId FROM gps_locations WHERE synced = 0 ORDER BY ts ASC LIMIT ?",
      [limit],
    );
    return rows.map((r) => ({
      localId: r.localId,
      lat: r.lat,
      lng: r.lng,
      ts: r.ts,
      speed: r.speed,
      heading: r.heading,
      accuracy: r.accuracy,
      battery: r.battery,
      ...(r.workOrderId ? { workOrderId: r.workOrderId } : {}),
    }));
  }

  /**
   * Delete the given points once the backend has accepted them. We delete
   * rather than flip `synced = 1` so the table stays small; the backend
   * deduplicates by timestamp anyway (idempotent writes).
   */
  async markSynced(localIds: string[]): Promise<void> {
    for (const id of localIds) {
      await this.db.run("DELETE FROM gps_locations WHERE localId = ?", [id]);
    }
  }

  /** Count of points still waiting to be sent. */
  async pendingCount(): Promise<number> {
    const rows = await this.db.query<{ cnt: number }>(
      "SELECT COUNT(*) as cnt FROM gps_locations WHERE synced = 0",
    );
    return rows[0]?.cnt ?? 0;
  }

  /** Remove every queued point (used on logout / clearAll). */
  async clearAll(): Promise<void> {
    await this.db.run("DELETE FROM gps_locations");
  }
}
